import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShoppingCart, User, ChevronDown, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth, useCart } from "@/App";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "Products", path: "/products" },
  { 
    name: "About", 
    children: [
      { name: "About Us", path: "/about" },
      { name: "About Microgreens", path: "/about-microgreens" },
    ]
  },
  { name: "E-Book", path: "/ebook" },
  { name: "Spotlight", path: "/spotlight" },
  { name: "Blog", path: "/blog" },
  { name: "Contact", path: "/contact" },
];

const ADMIN_EMAILS = ["admin@prarohagreens.com"];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { cart } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const cartItemCount = cart.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-soft"
          : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2" data-testid="logo-link">
            <img 
              src="/praroha-logo-header.png" 
              alt="Praroha Greens Logo"
              className="h-16 md:h-20 lg:h-24 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <DropdownMenu key={link.name}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-1 px-4 py-2 text-base font-medium text-foreground/80 hover:text-praroha-forest transition-colors"
                      data-testid={`nav-${link.name.toLowerCase()}-dropdown`}
                    >
                      {link.name}
                      <ChevronDown className="w-5 h-5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-48">
                    {link.children.map((child) => (
                      <DropdownMenuItem key={child.path} asChild>
                        <Link
                          to={child.path}
                          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                          className="w-full"
                          data-testid={`nav-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {child.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className={`px-4 py-2 text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? "text-praroha-forest"
                      : "text-foreground/80 hover:text-praroha-forest"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              )
            )}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Cart */}
            <Link
              to="/cart"
              className="relative p-2 text-foreground/80 hover:text-praroha-forest transition-colors"
              data-testid="cart-link"
            >
              <ShoppingCart className="w-5 h-5 md:w-6 md:h-6" />
              {cartItemCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-5 h-5 bg-praroha-terracotta text-white text-xs font-bold rounded-full flex items-center justify-center"
                  data-testid="cart-badge"
                >
                  {cartItemCount}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 p-2 text-foreground/80 hover:text-praroha-forest transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-5 h-5 md:w-6 md:h-6" />
                    <span className="hidden md:inline text-sm font-medium">
                      {user.name.split(" ")[0]}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {ADMIN_EMAILS.includes(user.email) && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2" data-testid="admin-dashboard-link">
                          <LayoutDashboard className="w-4 h-4" />
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem asChild>
                    <Link to="/profile" data-testid="profile-link">My Profile</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile">My Orders</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="text-destructive"
                    data-testid="logout-btn"
                  >
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="login-link">
                <Button
                  variant="outline"
                  size="sm"
                  className="hidden sm:flex rounded-full border-praroha-forest text-praroha-forest hover:bg-praroha-forest hover:text-white"
                >
                  Login
                </Button>
                <User className="w-5 h-5 sm:hidden text-foreground/80" />
              </Link>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2 text-foreground/80"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-x-0 top-20 bg-white border-t shadow-lg transition-all duration-300 ${
          mobileMenuOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-4 pointer-events-none"
        }`}
        style={{ maxHeight: "calc(100vh - 5rem)", overflowY: "auto" }}
        data-testid="mobile-menu"
      >
        <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.name} className="space-y-1">
                <div className="px-4 py-2 text-sm font-semibold text-muted-foreground">
                  {link.name}
                </div>
                {link.children.map((child) => (
                  <Link
                    key={child.path}
                    to={child.path}
                    onClick={() => { setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="block px-8 py-2 text-sm text-foreground/80 hover:text-praroha-forest hover:bg-muted rounded-lg transition-colors"
                  >
                    {child.name}
                  </Link>
                ))}
              </div>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => { setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className={`block px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  location.pathname === link.path
                    ? "bg-praroha-forest/10 text-praroha-forest"
                    : "text-foreground/80 hover:text-praroha-forest hover:bg-muted"
                }`}
              >
                {link.name}
              </Link>
            )
          )}
        </nav>
      </div>
    </header>
  );
}
